import { supabase } from '../clients/supabaseClient';
import { ORDER_STATUS } from '../constants/orderStatuses';

// Unassigns the picker from an order by reverting it to AWAITING_PACKING.
// Throws if a picking session is currently IN_PROGRESS.
//
// Flow mirrors driver unassign:
//   1. proc_log_status_history trigger writes a clean AWAITING_PACKING row
//      when orders.status is reverted.
//   2. We then insert a second AWAITING_PACKING row with the revoked picker
//      stamped as model_id — this shows as "AWAITING PACKING — Picker: [name]
//      PICKER UNASSIGNED" in the timeline, sitting between the two PACKER_ASSIGNED
//      entries just like ASSIGN_DRIVER (REVOKED) sits between two DRIVER_ASSIGNED.
export async function unassignPickerFromOrderInDb(orderId: string): Promise<void> {
    // Guard: block unassign if a picking session is in progress.
    const { data: activeSessions, error: sessionError } = await supabase
        .from('picking_sessions')
        .select('id, status')
        .eq('order_id', orderId)
        .eq('status', 'IN_PROGRESS')
        .limit(1);

    if (sessionError) throw sessionError;

    if (activeSessions && activeSessions.length > 0) {
        throw new Error('Cannot unassign picker — a picking session is currently in progress. The picker must complete or cancel their session first.');
    }

    // Capture the current picker before reverting.
    const { data: packerAssignedStatus } = await supabase
        .from('statuses')
        .select('id')
        .eq('name', ORDER_STATUS.PACKER_ASSIGNED)
        .single();

    const { data: currentPickerRow } = await supabase
        .from('order_status_history')
        .select('model_id')
        .eq('order_id', orderId)
        .eq('status_id', packerAssignedStatus?.id ?? '')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

    const revokedPickerId = currentPickerRow?.model_id ?? null;

    // Fetch AWAITING_PACKING status id.
    const { data: awaitingPackingStatus, error: statusError } = await supabase
        .from('statuses')
        .select('id')
        .eq('name', ORDER_STATUS.AWAITING_PACKING)
        .single();

    if (statusError || !awaitingPackingStatus) {
        throw new Error('Could not resolve AWAITING_PACKING status.');
    }

    // Revert order status — proc_log_status_history trigger writes a clean
    // AWAITING_PACKING history row automatically.
    const { error: updateError } = await supabase
        .from('orders')
        .update({ status: ORDER_STATUS.AWAITING_PACKING, status_id: awaitingPackingStatus.id })
        .eq('id', orderId);

    if (updateError) throw updateError;

    // Insert a second AWAITING_PACKING row stamped with the revoked picker —
    // this is the "PICKER UNASSIGNED" entry shown in the timeline between the
    // two PACKER_ASSIGNED entries, matching the driver ASSIGN_DRIVER (REVOKED) pattern.
    if (revokedPickerId) {
        await supabase.from('order_status_history').insert({
            order_id:   orderId,
            status_id:  awaitingPackingStatus.id,
            model_id:   revokedPickerId,
            model_type: 'Picker',
        });
    }
}